import { useMemo } from 'react';
import {
    exceedsGuestCapacity,
    selectedObjectCapacity,
} from '../utils/guestCapacity';

type GuestCapacityGuardArgs = {
    /** The object as it came from the availability list. */
    selectedObject: unknown;
    /** The same object from the object-details response, when loaded. */
    selectedObjectDetails: unknown;
    /** The guest count currently in the form. */
    guests: number | null | undefined;
    /**
     * The guest count the booking was saved with. Omit on create screens.
     *
     * On an edit screen only a count the operator changed here may block the
     * save: a booking can already carry a number above today's capacity —
     * because the capacity was lowered afterwards, or because it predates the
     * guard — and blocking those would hold hostage edits that never touch the
     * guest count. That is exactly what locked N-00000840 out of an internal
     * note. The warning still shows; only the save stays open.
     */
    savedGuests?: number | null;
};

/**
 * Explains the guest-capacity rule while the number is being typed.
 *
 * The rule itself is enforced behind the API (ADR-0020) — see
 * `utils/guestCapacity.ts` for the copies and why they exist. This hook only
 * decides what the screen says and whether Save stays enabled.
 */
export const useGuestCapacityGuard = ({
    selectedObject,
    selectedObjectDetails,
    guests,
    savedGuests,
}: GuestCapacityGuardArgs) =>
    useMemo(() => {
        const capacity = selectedObjectCapacity(
            selectedObjectDetails,
            selectedObject,
        );
        const exceeds = exceedsGuestCapacity({ guests: capacity }, guests);
        const changedHere =
            savedGuests === undefined || savedGuests === null
                ? true
                : Number(guests) !== Number(savedGuests);
        return { capacity, exceeds, blocksSave: exceeds && changedHere };
    }, [selectedObject, selectedObjectDetails, guests, savedGuests]);
